import type { ResultGroup } from './types';

export interface ResultSelectionControls {
  /** False when this user has no bulk action, so the checkboxes stay hidden. */
  enabled: boolean;
  active: boolean;
  isSelected: (recordId: string) => boolean;
  onToggle: (recordId: string, shiftKey: boolean) => void;
}

/** Record ids in the order grouped results are rendered. */
export function collectResultIds(groups: readonly ResultGroup[]): string[] {
  const ids: string[] = [];
  const walk = (group: ResultGroup) => {
    for (const child of group.children) walk(child);
    for (const result of group.results) ids.push(result.id);
  };
  for (const group of groups) walk(group);
  return ids;
}

export function toggleRecordSelection(
  visibleIds: readonly string[],
  selectedIds: ReadonlySet<string>,
  anchorId: string | null,
  recordId: string,
  shiftKey: boolean,
): { selectedIds: Set<string>; anchorId: string } {
  const next = new Set(selectedIds);
  if (shiftKey && anchorId) {
    const start = visibleIds.indexOf(anchorId);
    const end = visibleIds.indexOf(recordId);
    if (start !== -1 && end !== -1) {
      const from = Math.min(start, end);
      const to = Math.max(start, end);
      for (let index = from; index <= to; index += 1) {
        next.add(visibleIds[index]);
      }
      return { selectedIds: next, anchorId: recordId };
    }
  }
  if (next.has(recordId)) next.delete(recordId);
  else next.add(recordId);
  return { selectedIds: next, anchorId: recordId };
}

/** Select every visible record, or clear them when they are already all selected. */
export function toggleVisibleSelection(
  visibleIds: readonly string[],
  selectedIds: ReadonlySet<string>,
): Set<string> {
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const next = new Set(selectedIds);
  for (const id of visibleIds) {
    if (allSelected) next.delete(id);
    else next.add(id);
  }
  return next;
}

/** A click on the row background toggles selection. Links, buttons, and the checkbox do not. */
export function rowClickTogglesSelection(target: EventTarget | null): boolean {
  return !(target instanceof Element && Boolean(target.closest('a, button, input')));
}
